/**
 * Equipment detail → Pressure control (api#1268, BOP-1).
 *
 * SHOWN ONLY WHERE THE API SAYS IT APPLIES. GET /pressure-control/equipment/
 * :id/status answers `data: null` for equipment that is not a registered
 * pressure-control component, and that is an ordinary answer: the panel
 * renders nothing. No vertical guard — the module is cross-vertical, like
 * Equipment — and no query at all for a role the API would refuse.
 *
 * A failed read is said, not hidden: an unreachable server would otherwise look
 * exactly like "not a component", which on a BOP is the reassuring answer.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Gauge } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePcEquipmentStatus } from '../../hooks/usePressureControl';
import { canReadPc, pcClassLabel, PC_LIST } from '../../lib/pressure-control';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { PcCertStateSummary } from '../pressure-control/PcCertState';

export function PressureControlPanel({ equipmentId }: { equipmentId: string }) {
  const { user } = useAuth();
  const allowed = canReadPc(user?.role);
  const query = usePcEquipmentStatus(equipmentId, allowed);
  const failure = queryLoadFailure(query);

  if (!allowed || query.isLoading) return null;

  if (failure) {
    // A refusal for a role on the read list should not happen; say it if it does.
    return (
      <div className="bg-fieldiq-card rounded-lg border border-fieldiq-border p-4" data-testid="pc-panel">
        <div className="flex items-center gap-2">
          <Gauge size={18} className="text-fieldiq-accent" aria-hidden="true" />
          <h3 className="text-base font-bold text-white">Pressure control</h3>
        </div>
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="pressure-control status" className="py-6" />
      </div>
    );
  }

  const component = query.data?.component ?? null;
  if (!component) return null;

  return (
    <div className="bg-fieldiq-card rounded-lg border border-fieldiq-border p-4" data-testid="pc-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Gauge size={18} className="text-fieldiq-accent" aria-hidden="true" />
          <h3 className="text-base font-bold text-white">Pressure control</h3>
          <span className="text-sm text-fieldiq-muted">· {pcClassLabel(component.spec.componentClass)}</span>
        </div>
        <Link
          to={`${PC_LIST}/${component.equipmentId}`}
          className="inline-flex items-center gap-1 text-sm font-semibold text-fieldiq-text underline-offset-2 hover:underline"
        >
          Certificates and events
          <ChevronRight className="size-4" aria-hidden="true" />
        </Link>
      </div>
      <PcCertStateSummary state={component.certState} cert={component.currentCertification} />
    </div>
  );
}

export default PressureControlPanel;
